import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HandCoins, Eye, EyeOff, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import { getCommissionPending, entregarComision, deshacerEntregaComision, CommissionPending, money } from '../api';
import { useToast } from '../../../context/ToastContext';

/**
 * Comisión POR ENTREGAR (Mabel 5-oct-2026, video «comisiones Noli»: «vamos quitando otro Excel»).
 * Las cajeras le sacan a Noli del cajón su 50 % conforme pagan sus alumnas; el monto salía de un Excel.
 * Aquí sale solo: saldo ACUMULADO de los pagos de sus alumnas aún sin entregar (no por mes).
 *  - modo «inicio»: lo ve la cajera al entrar; importes tapados (el Inicio se ve en el mostrador).
 *  - modo «caja»: palomea los pagos y «Entregar» hace retiro de caja + gasto de nómina + marca, en un paso.
 *    Mabel (11:32): «si se registra en caja que automáticamente se registre en nómina» → ella ya no la captura.
 */
const CONCEPTO: Record<string, string> = { mensualidad: 'mensualidad', inscripcion: 'inscripción', curso: 'curso', clase_suelta: 'clase suelta', multa: 'pago tardío' };
const fecha = (d: string) => { const [, m, dd] = d.split('-').map(Number); return `${dd} ${['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][m - 1]}`; };

export default function ComisionPorEntregar({ modo, cajaAbierta = false, onDelivered }: { modo: 'inicio' | 'caja'; cajaAbierta?: boolean; onDelivered?: () => void }) {
  const toast = useToast();
  const [data, setData] = useState<CommissionPending[] | null>(null);
  const [sel, setSel] = useState<Record<number, Set<number>>>({});
  const [abierto, setAbierto] = useState(modo === 'caja');
  const [ver, setVer] = useState(modo === 'caja');
  const [confirmar, setConfirmar] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getCommissionPending()
      .then((r) => { setData(r.data); setSel(Object.fromEntries(r.data.map((t) => [t.user_id, new Set(t.items.map((i) => i.payment_id))]))); })
      .catch(() => setData((d) => d ?? []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const visibles = (data || []).filter((t) => t.items.length > 0 || t.deliveries.length > 0);
  if (!visibles.length) return null;
  const importe = (n: number) => (ver ? money(n) : '$ ****');

  async function entregar(t: CommissionPending) {
    const ids = [...(sel[t.user_id] || [])];
    setBusy(true);
    try {
      const r = await entregarComision(t.user_id, ids);
      toast.success(`Listo: ${money(r.amount)} entregados a ${t.name.split(' ')[0]} · salió de caja y quedó en nómina`);
      setConfirmar(null); load(); onDelivered?.();
    } catch (e: any) { toast.error(e?.message || 'No se pudo entregar'); }
    finally { setBusy(false); }
  }
  async function deshacer(id: number) {
    setBusy(true);
    try { await deshacerEntregaComision(id); toast.success('Entrega deshecha: se quitó el retiro de caja y el gasto'); load(); onDelivered?.(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo deshacer'); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-3" data-testid="comision-por-entregar">
      {visibles.map((t) => {
        const first = t.name.split(' ')[0];
        const s = sel[t.user_id] || new Set<number>();
        const elegidos = t.items.filter((i) => s.has(i.payment_id));
        const total = Math.round(elegidos.reduce((a, i) => a + i.commission, 0) * 100) / 100;
        const ult = t.deliveries[0];
        const toggle = (id: number) => setSel((p) => { const n = new Set(p[t.user_id]); n.has(id) ? n.delete(id) : n.add(id); return { ...p, [t.user_id]: n }; });
        return (
          <div key={t.user_id} className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-content">
                <HandCoins size={16} className="text-accent-ink" /> Comisión de {first}
                {modo === 'inicio' && (
                  <button type="button" onClick={() => setVer((v) => !v)} title={ver ? 'Ocultar importes' : 'Ver importes'} aria-label={ver ? 'Ocultar importes' : 'Ver importes'}
                    className="rounded-md p-1 text-content-3 transition hover:bg-surface-2 hover:text-content">
                    {ver ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                )}
              </h3>
              {modo === 'inicio' && t.items.length > 0 && (
                <Link to="/caja" className="shrink-0 text-xs font-medium text-brand-blue hover:underline">Entregar en Caja →</Link>
              )}
            </div>

            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-xs text-content-3">Por entregar</span>
              <span data-testid={`comision-pendiente-${t.user_id}`} className={`font-display text-2xl font-bold ${t.pending > 0 ? 'text-content' : 'text-content-3'}`}>{importe(t.pending)}</span>
              {t.items.length > 0 && (
                <span className="text-xs text-content-3">{t.pct}% de {importe(t.base)} · {t.items.length} pago{t.items.length === 1 ? '' : 's'} de sus alumnas</span>
              )}
            </div>
            {t.items.length === 0 && <p className="mt-1 text-xs text-content-3">Está al corriente: no hay pagos de sus alumnas sin entregar.</p>}

            {t.items.length > 0 && (
              <button onClick={() => setAbierto((v) => !v)} className="mt-2 flex items-center gap-1 text-xs font-medium text-content-2 hover:text-content">
                {abierto ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {abierto ? 'Ocultar' : 'Ver'} alumnas
              </button>
            )}
            {abierto && t.items.length > 0 && (
              <ul className="mt-2 divide-y divide-line rounded-xl border border-line">
                {t.items.map((i) => (
                  <li key={i.payment_id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                    {modo === 'caja' && (
                      <input type="checkbox" checked={s.has(i.payment_id)} onChange={() => toggle(i.payment_id)} data-testid={`comision-item-${i.payment_id}`} aria-label={`Entregar la de ${i.nombre}`} />
                    )}
                    <span className="min-w-0 flex-1 truncate text-content">{i.nombre}</span>
                    <span className="hidden text-xs text-content-3 sm:inline">{CONCEPTO[i.concept] || i.concept} · pagó {fecha(i.pay_date)}</span>
                    <span className="w-20 text-right tabular-nums text-content-2">{importe(i.commission)}</span>
                  </li>
                ))}
              </ul>
            )}

            {modo === 'caja' && t.items.length > 0 && (
              <div className="mt-3">
                {!cajaAbierta ? (
                  <p className="text-xs text-content-3">Abre la caja para entregarle su comisión.</p>
                ) : confirmar === t.user_id ? (
                  <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-content-2">
                    <span>¿Sacar <b className="text-content">{money(total)}</b> de caja para {first}? Queda como retiro y en su nómina.</span>
                    <span className="ml-auto flex gap-2">
                      <button onClick={() => setConfirmar(null)} className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-content-2 hover:bg-surface-2">Cancelar</button>
                      <button onClick={() => entregar(t)} disabled={busy} data-testid="comision-confirmar"
                        className="rounded-lg bg-accent-solid px-3 py-1.5 text-xs font-semibold text-on-accent hover:bg-accent-solid/90 disabled:opacity-60">{busy ? 'Entregando…' : 'Sí, entregar'}</button>
                    </span>
                  </div>
                ) : (
                  <button onClick={() => setConfirmar(t.user_id)} disabled={!elegidos.length} data-testid="comision-entregar"
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-accent-solid px-3 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
                    <HandCoins size={15} /> Entregar {money(total)} a {first}
                  </button>
                )}
              </div>
            )}

            {ult && (
              <div className="mt-3 flex flex-wrap items-center gap-x-2 text-xs text-content-3" data-testid="comision-ultima">
                Última entrega: {fecha(ult.delivered_at)} · {importe(ult.amount)}{ult.by_name ? ` · ${ult.by_name.split(' ')[0]}` : ''}
                {modo === 'caja' && ult.caja_abierta && (
                  <button onClick={() => deshacer(ult.id)} disabled={busy} data-testid="comision-deshacer"
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-content-2 underline-offset-2 hover:underline disabled:opacity-60">
                    <RotateCcw size={12} /> Deshacer
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
